import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { buildConductorPayload, validateConfig } from './conductorConfig'
import { getCircuitPower } from './CircuitsTable'

// Problemas que impedem o cálculo: { boardId, circuitId, message }. Circuitos sem nome ou com potência zero são
// destacados na tabela; IDRs sem circuitos são apenas avisados (ver collectEmptyIdrs).
function collectProblems(boards) {
  const problems = []

  for (const board of boards) {
    const boardProblem = validateConfig(board)

    if (boardProblem) {
      problems.push({
        boardId: board.id,
        circuitId: null,
        message: `Cabo de alimentação de ${board.name}: ${boardProblem}`,
      })
    }

    for (const circuit of board.circuits) {
      const label = `Circuito ${circuit.id} de ${board.name}`
      const configProblem = validateConfig(circuit)
      const message = !circuit.name?.trim()
        ? `${label}: informe o nome do circuito.`
        : getCircuitPower(circuit) <= 0
          ? `${label} (${circuit.name}): a potência é 0 VA; adicione elementos ao circuito.`
          : configProblem
            ? `${label}: ${configProblem}`
            : null

      if (message) problems.push({ boardId: board.id, circuitId: circuit.id, message })
    }
  }

  return problems
}

function collectEmptyIdrs(boards) {
  return boards.flatMap((board) =>
    (board.idrs || [])
      .filter((idr) => !board.circuits.some((circuit) => circuit.idrId === idr.id))
      .map((idr) => `${idr.id} (${board.name})`)
  )
}

export function useCalculation({ state, onCalculated }) {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()
  const [invalid, setInvalid] = useState({})
  const { boards, supplyType, allowsTwoPoles, demandRules, selectedSystem, temperatureFactor } = state

  // Qualquer edição dos quadros limpa o destaque dos circuitos com problema.
  useEffect(() => {
    setInvalid({})
  }, [boards])

  async function handleCalculate(event) {
    event.preventDefault()

    const problems = collectProblems(boards)

    if (problems.length > 0) {
      const nextInvalid = {}
      problems.forEach(({ boardId, circuitId }) => {
        if (circuitId !== null) nextInvalid[boardId] = new Set([...(nextInvalid[boardId] || []), circuitId])
      })
      setInvalid(nextInvalid)
      state.selectBoard(problems[0].boardId)
      toast.error(
        problems.length === 1
          ? problems[0].message
          : `${problems[0].message} (e mais ${problems.length - 1} problema${problems.length === 2 ? '' : 's'}; as linhas em vermelho precisam de ajuste)`
      )
      return
    }

    const emptyIdrs = collectEmptyIdrs(boards)

    if (emptyIdrs.length > 0) {
      toast.warn(`IDR sem circuitos vinculados: ${emptyIdrs.join(', ')}. Eles não terão corrente selecionada.`)
    }

    try {
      const data = {
        selectedSystem,
        supplyType,
        temperatureFactor: Number(temperatureFactor),
        demandRules,
        boards: boards.map((board) => ({
          id: board.id,
          name: board.name,
          parentId: board.parentId,
          ...buildConductorPayload(board),
          // O neutro carregado só existe em alimentação trifásica; nos demais casos o campo é omitido.
          ...(supplyType === 'three-phase' && { neutralLoaded: Boolean(board.neutralLoaded) }),
          // Os vínculos ficam em cada circuito (idrId); os índices são calculados aqui, a cada envio.
          idrs: (board.idrs || []).map((idr) => ({
            id: idr.id,
            name: idr.name,
            circuitIndexes: board.circuits.reduce(
              (indexes, circuit, index) => (circuit.idrId === idr.id ? [...indexes, index] : indexes),
              []
            ),
          })),
          circuits: board.circuits.map(({ demandRuleId, conductor, grouping, groupFactor, ...circuit }) => ({
            ...circuit,
            // O vínculo com o IDR já foi convertido em circuitIndexes; undefined é omitido do JSON.
            idrId: undefined,
            ...buildConductorPayload({ conductor, grouping, groupFactor }),
            isTwoPoles: allowsTwoPoles && circuit.isTwoPoles,
            ...(demandRules.some((rule) => rule.id === demandRuleId) && { demandRuleId }),
          })),
        })),
      }

      const response = await api.post('/electrical/boards', data)
      state.setResult(response.data)
      state.setIsStale(false)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()
      toast.success('Cálculo atualizado')
      onCalculated?.()
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    }
  }

  return { canCalculate, handleCalculate, invalid }
}
